"use client";

import { displayDateTime } from '@/utils/displayDate';
import { createContentReviewValidation } from '@/validations/ContentValidation';
import { Input, Textarea } from '@nextui-org/react'
import React, { memo, useCallback, useEffect, useState, useTransition, type Dispatch, type SetStateAction } from 'react'
import { useForm } from "react-hook-form";
import { type z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/Form";
import { createContentReview, getContentReviewsByContentId } from 'actions/content';
import useSWR from 'swr';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronDown } from 'lucide-react';
import Loading from '@/components/Loading';
import { createAvatar } from '@dicebear/core';
import { thumbs } from '@dicebear/collection';
import Image from 'next/image';

interface IComment {
  email: string;
  comment: string;
  createAt: string
}

const Comment = memo(function Comment({ comment }: { comment: IComment }) {
  const isLong = comment.comment.length > 100
  const [showMore, setShowMore] = useState(false)
  const eyeVariants = [
    "variant1W10", "variant1W12", "variant1W14", "variant1W16",
    "variant2W10", "variant2W12", "variant2W14", "variant2W16",
    "variant3W10", "variant3W12", "variant3W14", "variant3W16",
    "variant4W10", "variant4W12", "variant4W14", "variant4W16",
    "variant5W10", "variant5W12", "variant5W14", "variant5W16",
    "variant6W10", "variant6W12", "variant6W14", "variant6W16",
    "variant7W10", "variant7W12", "variant7W14", "variant7W16",
    "variant8W10", "variant8W12", "variant8W14", "variant8W16",
    "variant9W10", "variant9W12", "variant9W14", "variant9W16"
  ] as const;

  const avatar = createAvatar(thumbs, {
    seed: Math.random() > 0.5 ? "Felix" : "Aneka",
    randomizeIds: true,
    size: 32,
    radius: 1000,
    flip: Math.random() > 0.5,
    scale: 100,
    eyes: [eyeVariants[Math.floor(Math.random() * eyeVariants.length)] ?? "variant1W10"],
    backgroundColor: ["#3D3D3D"],
  });

  return (
    <div className='flex flex-row items-start space-x-3'>
      <div className='min-w-[32px] max-w-[32px] h-[32px] bg-[#3D3D3D] 
            rounded-full flex items-center justify-center'>
        {/* <span className='h5 !text-sm font-bold text-white !leading-none'>{Array.from(comment.email)[0]}</span> */}
        <Image
          src={avatar.toDataUri()}
          alt='avatar'
          width={32}
          height={32}
          className='rounded-full'
        />
      </div>
      <div className='flex flex-col items-start space-y-[3px]'>
        <div className='space-x-2 mt-[2px]'>
          <b>{comment.email}</b>
          <span className='text-[13px] text-[#888]'>{displayDateTime(comment.createAt, "DD MM YYYY")}</span>
        </div>
        <p>
          {
            isLong && !showMore ?
              <>
                {comment.comment.slice(0, 300).trimEnd() + "..."}
                {" "}
                <button
                  className=' font-semibold hover:underline'
                  onClick={() => setShowMore(prev => !prev)}
                >
                  See more
                </button>
              </> : comment.comment
          }
        </p>
      </div>
    </div>
  )
});

function CreateComment(params: { textContentId: string, setTempComment: Dispatch<SetStateAction<IComment[] | undefined>> }) {
  const [, startTransition] = useTransition();
  const [, setErrorMessage] = useState<string | null>(null);

  const form = useForm<z.infer<typeof createContentReviewValidation>>({
    resolver: zodResolver(createContentReviewValidation),
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      review: "",
      textContentId: params.textContentId
    }
  });

  const onSubmit = useCallback(
    (values: z.infer<typeof createContentReviewValidation>) => {
      setErrorMessage("")

      if (!values.email || !values.review || !values.review.trim()) {
        form.setError("review", { message: "Please enter review" })
        return
      }

      startTransition(() => {
        createContentReview(values)
          .then(() => {
            params.setTempComment((prev) => {
              return [
                ...(prev ?? []),
                {
                  email: values.email,
                  comment: values.review,
                  createAt: new Date().toDateString()
                }
              ]
            })
          })
          .catch(() => {
            setErrorMessage("Something went wrong!")
          })
          .finally(() => {
            form.reset({ email: values.email, review: "", textContentId: values.textContentId })
          })
      });
    },
    []
  );

  return (
    <div className='mt-[12px] bg-[#F6F6F6] hover:!bg-[#E7E7E7] rounded-[12px] p-[12px] space-y-[16px]'>
      <Form {...form}>
        <form
          className="space-y-3 w-full  rounded-xl "
          onSubmit={form.handleSubmit(onSubmit)}
          id="updatecontentform"
        >
          <FormField
            control={form.control}
            name="review"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder='Write your comment here...'
                    classNames={{
                      inputWrapper: "!bg-transparent shadow-none !p-0",
                      innerWrapper: "bg-transparent"
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className='flex flex-row space-x-3 items-start justify-end '>
            <div className='flex-1'>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        size="sm"
                        type='email'
                        label="Email"
                        className="max-w-full"
                        classNames={{
                          inputWrapper: "shadow-none rounded-[12px] bg-white hover:bg-white",
                          innerWrapper: "bg-transparent"
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <button className='bg-black text-white font-semibold px-[24px] h-[46px] rounded-lg text-[14px]'
              type='submit'
            >
              <span>Submit</span>
            </button>
          </div>
        </form>
      </Form>
    </div>
  )
}

export function Comments(params: { textContentId: string }) {
  const [tempComment, setTempComment] = useState<IComment[]>()
  const [allComments, setAllComments] = useState<IComment[]>()
  const [page, setPage] = useState(1)
  const comments = useSWR(`/api/content/${params.textContentId}/comments?page=` + page, async () => {
    return await getContentReviewsByContentId({ textContentId: params.textContentId, page, take: 4 })
  }, { revalidateOnFocus: false })

  const loadmore = () => {
    setPage(page + 1);
  }

  useEffect(() => {
    if (comments.data) {
      setAllComments((prev) => {
        const mappedComments = comments.data?.comments?.map(c => ({
          email: c.email,
          comment: c.review,
          createAt: c.createAt.toDateString()
        })) ?? [];
        return [...(prev ?? []), ...mappedComments];
      });
    }
  }, [comments.data?.currentPage]);

  return (
    <div className='w-full'>
      <CreateComment textContentId={params.textContentId} setTempComment={setTempComment} />
      {
        (allComments?.length !== 0 || tempComment) && (
          <div className='flex flex-col w-full mt-8 space-y-6'>
            <h3 className=" h4 !text-[32px] leading-none flex items-center space-x-2">
              <span>Comments</span>
              <span className='bg-[#454545] text-[18px] font-dmSans px-[8px] rounded-lg text-white'>
                {comments.data?.totalComments ?? 0 + (tempComment?.length || 0)}
              </span>
            </h3>
            {tempComment?.length && tempComment.map((tempComment) => (
              <Comment comment={tempComment} />
            ))}
            {
              allComments?.map((comment) => (
                <Comment key={comment.createAt} comment={{
                  comment: comment.comment,
                  email: comment.email,
                  createAt: comment.createAt
                }} />
              ))
            }
          </div>
        )
      }
      <div className='w-full flex items-center justify-center my-10'>
        {
          (comments.isLoading || comments.isValidating) ? (
            <button className='flex flex-flex-row space-x-2 items-center justify-center space-y-[1px] hover:bg-[#F6F6F6] p-4 rounded-lg' onClick={loadmore}>
              <Loading size='sm' />
            </button>
          ) : (
            allComments?.length !== comments.data?.totalComments && (
              <button className='flex flex-flex-row space-x-2 items-center justify-center space-y-[1px] hover:bg-[#F6F6F6] p-4 rounded-lg' onClick={loadmore}>
                <span className='font-medium '>Read more</span>
                <ChevronDown className='w-[16px] h-[16px]' />
              </button>
            )
          )
        }
      </div>
    </div>
  )
}
